import { ITestCase } from "./ITestCase";
import { getMetaGatewayUrl, randomString } from "../Utils";
import { Features, Services } from "./Modules";
import { checkGarconUrl, whipDeleteRequest, whepDeleteRequest, patchRequest, whepRequest, whipRequest } from "./Utils";

import Delay from "../client/Delay";
import * as rtc_stat from '../client/WebRTCStatistics'

const producerPeer = async () => {
    const pc = new RTCPeerConnection();

    const userMedia = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: true
    });

    const audioTransceiver = pc.addTransceiver("audio", {
      direction: "sendonly", 
      streams: [userMedia],
      sendEncodings: [{
        adaptivePtime: true,
      } as RTCRtpEncodingParameters]
    })

    let videoTransceiver = pc.addTransceiver("video", {
      direction: "sendonly", 
      streams: [userMedia],
    })

    audioTransceiver.sender.replaceTrack(userMedia.getAudioTracks()[0]);
    videoTransceiver.sender.replaceTrack(userMedia.getVideoTracks()[0]);

    return pc;
}

const consumerPeer = async () => {
    const pc = new RTCPeerConnection();

    pc.addTransceiver("audio")

    pc.addTransceiver("video")

    return pc;
}

interface JoinWithWhipWhepParams {
  clientRegion?: string;
  clientRegionSubdivision?: string;
}


export default class JoinWithWhipWhep implements ITestCase {
    private readonly params: JoinWithWhipWhepParams;

    readonly tags: Set<string> = new Set([
        Features.PushToTalk,
        Services.Garcon,
        Services.Coturn,
        Services.Kvashanina,
        Services.Kaldun,
        Services.Mediasoup,
        Services.DzikiLaz,
    ]);

    get name(): string {
      return "Join room to produce with WHIP and consume with WHEP " + ((this.params.clientRegion || this.params.clientRegionSubdivision) ? `in ${this.params.clientRegion}/${this.params.clientRegionSubdivision} region` : ``);
    };

    constructor(params: JoinWithWhipWhepParams) {
      this.params = params;
    }

    async callback(assert: Assert): Promise<void> {
        const roomId = randomString();
        const [producerId, consumerId] = [randomString(), randomString()];

        console.log(`room id: ${roomId}, producer id: ${producerId} consumer id: ${consumerId}`);

        const metaGatewayUrl = checkGarconUrl(assert, await getMetaGatewayUrl());

        const producer = await producerPeer();

        const producerOffer = await producer.createOffer();

        console.log("initial producer offer: ", producerOffer.sdp);

        const patchedProducerOffer = await patchRequest(producerOffer, metaGatewayUrl, assert);

        console.log("patched producer offer: ", patchedProducerOffer.sdp);

        await producer.setLocalDescription(patchedProducerOffer);

        console.log("effective producer local description: ", producer.localDescription?.sdp);

        const whipResponse = await whipRequest(assert, metaGatewayUrl, {
          userId: producerId,
          roomId: roomId,
          offer: patchedProducerOffer.sdp,
          clientRegion: this.params.clientRegion,
          clientRegionSubdivision: this.params.clientRegionSubdivision,
        });

        console.log("producer whip answer: ", whipResponse.sdp);

        await producer.setRemoteDescription({sdp: whipResponse.sdp, type: "answer"});

        console.log("effective producer remote description: ", producer.remoteDescription?.sdp);

        const consumer = await consumerPeer();

        const consumerOffer = await consumer.createOffer();

        console.log("initial consumer offer: ", consumerOffer.sdp);

        const patchedConsumerOffer = await patchRequest(consumerOffer, metaGatewayUrl, assert);

        console.log("patched consumer offer: ", patchedConsumerOffer.sdp);

        await consumer.setLocalDescription(patchedConsumerOffer);

        console.log("effective consumer local description: ", consumer.localDescription?.sdp);

        const whepResponse = await whepRequest(assert, metaGatewayUrl, {
          userId: consumerId,
          roomId: roomId,
          broadcasterId: producerId,
          offer: patchedConsumerOffer.sdp,
          clientRegion: this.params.clientRegion,
          clientRegionSubdivision: this.params.clientRegionSubdivision,
        });

        console.log("consumer whep answer: ", whepResponse.sdp);

        await consumer.setRemoteDescription({sdp: whepResponse.sdp, type: "answer"});

        console.log("effective consumer remote description: ", consumer.remoteDescription?.sdp);

        const inboundVideoRtpFilter = (report: rtc_stat.RTCStatsReport): boolean => {
          return report.type === 'inbound-rtp' && report.kind === 'video'
        };

        const consumerInitialState = await consumer.getStats();

        const consumerInitialVideoStat = Array.from(consumerInitialState.values()).find(inboundVideoRtpFilter) as rtc_stat.RTCInboundRtpStreamStats | undefined;

        await Delay(5000);

        assert.true(consumer.connectionState === "connected", "Consumer connection state is connected");
        assert.true(producer.connectionState === "connected", "Producer connection state is connected");

        const consumerFinalState = await consumer.getStats();

        const consumerFinalVideoStat = Array.from(consumerFinalState.values()).find(inboundVideoRtpFilter) as rtc_stat.RTCInboundRtpStreamStats | undefined;

        console.log("Consumer initial video statistics", consumerInitialVideoStat);
        console.log("Consumer final video statistics", consumerFinalVideoStat);

        if (consumerFinalVideoStat && consumerFinalVideoStat.kind === "video" && consumerInitialVideoStat && consumerInitialVideoStat.kind === "video") {
          assert.true(consumerFinalVideoStat.framesDecoded! - consumerInitialVideoStat.framesDecoded! > 0, "Consumer final video statistics is greater than initial");
        }

        consumer.close();
        producer.close();

        await whipDeleteRequest(assert, metaGatewayUrl, {
          userId: producerId,
          roomId: roomId,
          sessionUrl: whipResponse.location,
          clientRegion: this.params.clientRegion,
          clientRegionSubdivision: this.params.clientRegionSubdivision,
        });

        await whepDeleteRequest(assert, metaGatewayUrl, {
          userId: consumerId,
          roomId: roomId,
          broadcasterId: producerId,
          sessionUrl: whepResponse.location,
          clientRegion: this.params.clientRegion,
          clientRegionSubdivision: this.params.clientRegionSubdivision,
        });

        console.log(`"${this.name}" test finished`);
    }
}
